import { useCallback, useLayoutEffect, useMemo, useRef } from 'react';
import debounce from 'lodash-es/debounce';

/**
 * Custom hook that will debounce any given function.
 *
 * @param {Function} callback
 * @param {number} delay
 * @param {Object} [options]
 * @param {boolean} [options.leading=false]
 * @param {boolean} [options.trailing=true]
 * @param {number} [options.maxWait]
 *
 * @returns {Function & { cancel: Function, flush: Function }}
 */
export const useDebounce = (callback, delay, options = {}) => {
  const callbackRef = useRef(callback);
  const debouncedRef = useRef(null);

  const { leading = false, trailing = true, maxWait } = options;

  useLayoutEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  useLayoutEffect(() => {
    const debounceOptions = {
      leading,
      trailing,
      ...(maxWait !== undefined ? { maxWait } : {}),
    };

    const debounced = debounce((...args) => callbackRef.current(...args), delay, debounceOptions);

    debouncedRef.current = debounced;

    return () => {
      debounced.cancel();
      debouncedRef.current = null;
    };
  }, [delay, leading, trailing, maxWait]);

  const invoke = useCallback((...args) => {
    return debouncedRef.current?.(...args);
  }, []);

  const cancel = useCallback(() => {
    debouncedRef.current?.cancel();
  }, []);

  const flush = useCallback(() => {
    return debouncedRef.current?.flush();
  }, []);

  const debouncedCallback = useMemo(() => {
    const callable = (...args) => invoke(...args);

    // eslint-disable-next-line react-hooks/immutability
    callable.cancel = cancel;
    // eslint-disable-next-line react-hooks/immutability
    callable.flush = flush;

    return callable;
  }, [invoke, cancel, flush]);

  return debouncedCallback;
};
